import axios from "axios";
import moment, { Moment } from "moment";
import { useState } from "react";

import { OpenInNew } from "@mui/icons-material";
import {
	Alert,
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Link,
	Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import ThemedDialog from "../../Components/ThemedDialog";
import { LeasmanVehicle, Vehicle } from "../../system/Domain";

interface Props {
	open: boolean;
	vehicle: Vehicle;
	leasmanVehicle?: LeasmanVehicle;
	onClose: () => void;
	onSaved: (vehicle: Vehicle) => void;
}

const CorrectPlateNumberDialog = ({ open, vehicle, leasmanVehicle, onClose, onSaved }: Props) => {
	const [since, setSince] = useState<Moment | null>(null);
	const [saving, setSaving] = useState(false);
	const [openingZlb2, setOpeningZlb2] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const newPlateNumber = leasmanVehicle?.plateNumber;

	const syncedToday = !!vehicle.remarketing.leasmanContract?.dateReferenceCreated
		&& moment(vehicle.remarketing.leasmanContract.dateReferenceCreated).isSame(moment(), "day");

	const isValid = !!since && !!newPlateNumber && syncedToday;

	const openZlb2 = async () => {
		setOpeningZlb2(true);
		// Open the tab synchronously (still inside the click gesture) so Chrome doesn't
		// treat the later, async navigation to the blob URL as a popup and block it.
		const newTab = window.open("", "_blank");
		try {
			const response = await axios.get(`/api/vehicles/${vehicle.id}/documents/zlb2.pdf`, {
				responseType: "blob",
			});
			// The backend serves this as application/octet-stream, so the type must be
			// set explicitly here for the browser to recognize and display it as a PDF.
			const blob = new Blob([response.data], { type: "application/pdf" });
			const blobUrl = window.URL.createObjectURL(blob);

			if (newTab) {
				newTab.location.href = blobUrl;
			} else {
				window.open(blobUrl, "_blank");
			}
		} catch (e) {
			newTab?.close();
			throw e;
		} finally {
			setOpeningZlb2(false);
		}
	};

	const handleSave = async () => {
		if (!isValid) {
			return;
		}

		setSaving(true);
		setError(null);

		try {
			const { data } = await axios.post<Vehicle>(`/api/vehicles/${vehicle.id}/correct-plate-number`, {
				since: since!.toISOString(),
			});
			onSaved(data);
		} catch (e: any) {
			setError(e.response?.data ?? e.message ?? "Speichern fehlgeschlagen");
		} finally {
			setSaving(false);
		}
	};

	return (
		<ThemedDialog open={open} maxWidth="sm" fullWidth onClose={onClose}>
			<DialogTitle>Kennzeichen korrigieren</DialogTitle>
			<DialogContent>
				<Typography gutterBottom>
					Aktuelles Kennzeichen: <strong>{vehicle.plateNumber}</strong>
				</Typography>
				<Typography gutterBottom>
					Neues Kennzeichen (aus Leasman-Vertrag): <strong>{newPlateNumber ?? "-"}</strong>
				</Typography>

				{!newPlateNumber && (
					<Alert severity="error" sx={{ mt: 1, mb: 2 }}>
						Für dieses Fahrzeug ist kein Leasman-Kennzeichen hinterlegt.
					</Alert>
				)}

				{!syncedToday && (
					<Alert severity="error" sx={{ mt: 1, mb: 2 }}>
						Die Leasman Synchronisation wurde heute noch nicht durchgeführt. Bitte zuerst synchronisieren.
					</Alert>
				)}

				<Alert severity="warning" sx={{ mt: 1, mb: 2 }}>
					Bitte das Datum unbedingt anhand der ZLB II prüfen, bevor die Korrektur gespeichert wird.
				</Alert>

				<Link
					component="button"
					type="button"
					onClick={openZlb2}
					disabled={openingZlb2}
					underline="hover"
					sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}
				>
					<OpenInNew fontSize="small" />
					ZLB II in neuem Tab öffnen
				</Link>

				<FormControl fullWidth variant="standard">
					<DatePicker
						format="DD.MM.YYYY"
						label="Datum (gemäß ZLB II)"
						slotProps={{ textField: { variant: "standard" } }}
						value={since}
						onChange={(value: Moment | null) => setSince(value)}
					/>
				</FormControl>

				{error && (
					<Typography variant="body2" color="error" sx={{ mt: 2 }}>
						{error}
					</Typography>
				)}
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" color="secondary" onClick={onClose} href="" disabled={saving}>
					Abbrechen
				</Button>
				<Button
					variant="outlined"
					color="primary"
					onClick={handleSave}
					href=""
					disabled={!isValid || saving}
				>
					Speichern
				</Button>
			</DialogActions>
		</ThemedDialog>
	);
};

export default CorrectPlateNumberDialog;
